// src/pages/Login.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUp, confirmSignUp, forgotPassword, forgotPasswordSubmit } from '../services/AuthService';
import { useAuth } from '../components/AuthContext';
import './Login.css';

enum AuthState {
  SIGN_IN,
  SIGN_UP,
  CONFIRM_SIGN_UP,
  FORGOT_PASSWORD,
  RESET_PASSWORD
}

const Login: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>(AuthState.SIGN_IN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    
    try {
      switch (authState) {
        case AuthState.SIGN_IN:
          await signIn(email, password);
          await refreshUser();
          navigate('/dashboard');
          break;
          
        case AuthState.SIGN_UP:
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          await signUp(email, password, name);
          setMessage('Please check your email for a verification code');
          setAuthState(AuthState.CONFIRM_SIGN_UP);
          break;
          
        case AuthState.CONFIRM_SIGN_UP:
          await confirmSignUp(email, code);
          setMessage('Account confirmed successfully! You can now sign in.');
          setAuthState(AuthState.SIGN_IN);
          break;
          
        case AuthState.FORGOT_PASSWORD:
          await forgotPassword(email);
          setMessage('Password reset code sent to your email');
          setAuthState(AuthState.RESET_PASSWORD);
          break;
          
        case AuthState.RESET_PASSWORD:
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          await forgotPasswordSubmit(email, code, password);
          setMessage('Password reset successfully! You can now sign in.');
          setAuthState(AuthState.SIGN_IN);
          break;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const renderForm = () => {
    switch (authState) {
      case AuthState.SIGN_IN:
        return (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            
            <div className="auth-links">
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.SIGN_UP)} 
                className="link-button"
              >
                Create an account
              </button>
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.FORGOT_PASSWORD)} 
                className="link-button"
              >
                Forgot password?
              </button>
            </div>
          </>
        );
        
      case AuthState.SIGN_UP:
        return (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
            
            <div className="auth-links">
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.SIGN_IN)} 
                className="link-button"
              >
                Already have an account? Sign in
              </button>
            </div>
          </>
        );
        
      case AuthState.CONFIRM_SIGN_UP:
        return (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Account'}
            </button>
            
            <div className="auth-links">
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.SIGN_IN)} 
                className="link-button"
              >
                Back to Sign In
              </button>
            </div>
          </>
        );
        
      case AuthState.FORGOT_PASSWORD:
        return (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            
            <div className="auth-links">
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.SIGN_IN)} 
                className="link-button"
              >
                Back to Sign In
              </button>
            </div>
          </>
        );
        
      case AuthState.RESET_PASSWORD:
        return (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="code">Reset Code</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            
            <div className="auth-links">
              <button 
                type="button" 
                onClick={() => setAuthState(AuthState.SIGN_IN)} 
                className="link-button"
              >
                Back to Sign In
              </button>
            </div>
          </>
        );
    }
  };
  
  return (
    <div className="login-page">
      <div className="auth-container">
        <h2>
          {authState === AuthState.SIGN_IN && 'Sign In'}
          {authState === AuthState.SIGN_UP && 'Create Account'}
          {authState === AuthState.CONFIRM_SIGN_UP && 'Verify Account'}
          {authState === AuthState.FORGOT_PASSWORD && 'Reset Password'}
          {authState === AuthState.RESET_PASSWORD && 'Set New Password'}
        </h2>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <form onSubmit={handleSubmit}>
          {renderForm()}
        </form>
      </div>
    </div>
  );
};

export default Login;